import { useState, useEffect } from "react";
import { Input, Select, Radio, theme } from "antd";
import {
    EditOutlined,
    CheckOutlined,
    CloseOutlined,
    PhoneOutlined,
} from "@ant-design/icons";

type FieldType = "input" | "select" | "multi-select" | "radio";

interface Option {
    label: string;
    value: string;
}

interface Props {
    label?: string;
    type?: FieldType;
    value?: any;
    options?: Option[];
    onChange?: (val: any) => void;
    /** Hiện nút "Gọi điện" cạnh giá trị (gọi qua softphone dùng chung) */
    onCall?: () => void;
}

export default function EditableField({
    label,
    type = "input",
    value,
    options = [],
    onChange,
    onCall,
}: Props) {
    const { token } = theme.useToken();
    const [editing, setEditing] = useState(false);
    const [tempValue, setTempValue] = useState(value);

    useEffect(() => {
        setTempValue(value);
    }, [value]);

    const handleSave = () => {
        onChange?.(tempValue);
        setEditing(false);
    };

    const handleCancel = () => {
        setTempValue(value);
        setEditing(false);
    };

    const renderField = () => {
        switch (type) {
            case "select":
                return <Select value={tempValue} onChange={setTempValue} options={options} className="w-[200px]" size="small" />;
            case "multi-select":
                return <Select mode="multiple" value={tempValue} onChange={setTempValue} options={options} className="w-[250px]" size="small" />;
            case "radio":
                return (
                    <Radio.Group value={tempValue} onChange={(e) => setTempValue(e.target.value)}>
                        {options.map((opt) => (
                            <Radio key={opt.value} value={opt.value}>{opt.label}</Radio>
                        ))}
                    </Radio.Group>
                );
            default:
                return (
                    <Input
                        autoFocus
                        value={tempValue}
                        onChange={(e) => setTempValue(e.target.value)}
                        size="small"
                        className="w-[220px]"
                        onPressEnter={handleSave}
                        onKeyDown={(e) => { if (e.key === "Escape") handleCancel(); }}
                    />
                );
        }
    };

    return (
        <div className="flex justify-between items-start">
            {label && (
                <div className="w-40 text-sm" style={{ color: token.colorTextSecondary }}>
                    {label}
                </div>
            )}

            <div className="flex-1 flex items-center gap-2 flex-wrap">
                {!editing ? (
                    <>
                        <div className="text-[18px] font-bold">
                            {value && value.length !== 0 ? (
                                Array.isArray(value) ? value.join(", ") : value
                            ) : (
                                <span style={{ color: token.colorTextTertiary }}>—</span>
                            )}
                        </div>

                        <EditOutlined
                            style={{ color: token.colorPrimary }}
                            className="cursor-pointer text-[18px]"
                            onClick={() => setEditing(true)}
                        />

                        {onCall && (
                            <button type="button" onClick={onCall} className="bg-green-500 hover:bg-green-600 text-white px-3 py-1 rounded text-sm font-medium flex items-center gap-1 transition-colors ml-2 cursor-pointer">
                                <PhoneOutlined rotate={90} style={{ fontSize: '16px', color: '#fff' }} /> Gọi điện
                            </button>
                        )}
                    </>
                ) : (
                    <>
                        {renderField()}
                        <CheckOutlined className="cursor-pointer" style={{ color: token.colorSuccess }} onClick={handleSave} />
                        <CloseOutlined className="cursor-pointer" style={{ color: token.colorError }} onClick={handleCancel} />
                    </>
                )}
            </div>
        </div>
    );
}
